import React from 'react';
import * as joint from '@joint/plus';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import {CanvasEnvironment} from '../../../utils/canvas/canvas-environment';
import {useWorkspaceContext} from '../../../context/workspaceContext';
import {CanvasActionType} from '../../../@types/canvas-action-type';
import * as canvasActions from '../../../utils/canvas/canvas-actions';
import * as canvasUtils from '../../../utils/canvas/node-utils';

/**
 * Right-click menu for a Component's fault tree canvas -- the structural-editing subset of
 * {@link FaultTreeContextMenu}. No Events, no quantification / tags / callouts / report-mapping
 * (those arrive with their Component backends). "Add from Component" is intentionally absent --
 * it will be replaced by the TransferGate feature.
 */
export default function ComponentFaultTreeContextMenu(props: {
    elementView: joint.dia.ElementView | null;
    open: boolean;
    handleClose: () => void;
    env: CanvasEnvironment | null;
}): React.ReactElement {
    const {elementView, open, handleClose, env} = props;
    const {setCurrentCanvasAction} = useWorkspaceContext();
    const element = elementView ? elementView.model : null;
    const anchorEl = elementView ? (elementView.el as SVGElement) : null;
    const data = element?.get('data');

    const canEdit = (): boolean => canvasActions.canAddOrEditResource(env);
    const isFaultTreeNode = (): boolean => element?.get('nodeClass') === 'FAULTTREENODE';
    const isRoot = (): boolean => data != null && (data.parentId === null || data.parentId === undefined);

    const cannotAddNode = (): boolean => !canEdit() || data?.type === 'BASE_EVENT';
    const cannotPasteSubtree = (): boolean => !canEdit() || !data || !canvasUtils.canPasteFaultTreeNode(data);
    const cannotReorder = (): boolean =>
        !canEdit() || !data || !data.childIds || data.childIds.length <= 1;

    const dispatch = (actionType: CanvasActionType): void => {
        setCurrentCanvasAction({actionType});
        handleClose();
    };

    if (!isFaultTreeNode()) {
        return <div />;
    }

    return (
        <div>
            <Menu
                id="component-fault-tree-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{vertical: 'top', horizontal: 'right'}}
                slotProps={{
                    root: {
                        onContextMenu: (e: React.MouseEvent): void => e.preventDefault(),
                    },
                }}
            >
                <MenuItem
                    onClick={(): void => dispatch(CanvasActionType.FAULTTREENODE_EDIT_NODE)}
                    disabled={!canEdit()}
                >
                    Properties
                </MenuItem>
                <Divider />
                <MenuItem
                    onClick={(): void => dispatch(CanvasActionType.FAULTTREENODE_ADD_NODE)}
                    disabled={cannotAddNode()}
                >
                    Add Node
                </MenuItem>
                <MenuItem
                    onClick={(): void => dispatch(CanvasActionType.FAULTTREENODE_COPY_SUBTREE)}
                    disabled={!canEdit()}
                >
                    Copy SubTree
                </MenuItem>
                <MenuItem
                    onClick={(): void => dispatch(CanvasActionType.FAULTTREENODE_PASTE_SUBTREE)}
                    disabled={cannotPasteSubtree()}
                >
                    Paste SubTree
                </MenuItem>
                <MenuItem
                    onClick={(): void => dispatch(CanvasActionType.FAULTTREENODE_REORDER_CHILDREN)}
                    disabled={cannotReorder()}
                >
                    Reorder Children
                </MenuItem>
                <Divider />
                <MenuItem
                    onClick={(): void => dispatch(CanvasActionType.FAULTTREENODE_DELETE_NODE)}
                    disabled={!canEdit() || isRoot()}
                >
                    Delete Node
                </MenuItem>
            </Menu>
        </div>
    );
}
